import React, { useCallback, useEffect, useRef, useState } from 'react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { accountingApi } from '../../services/accountingApi';
import AccountingFilters from '../../components/accounting/AccountingFilters';
import AccountingKpis from '../../components/accounting/AccountingKpis';
import AccountingMovementCharts from '../../components/accounting/AccountingMovementCharts';
import MovementForm from '../../components/accounting/MovementForm';
import MovementsTable from '../../components/accounting/MovementsTable';
import '../../styles/accounting.css';
import { downloadAccountingExcel } from '../../utils/accountingExcel';

const todayLima = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'America/Lima', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

export default function AccountingMovements() {
    const [meta, setMeta] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [modalKey, setModalKey] = useState(0);
    const dirtyRef = useRef(false);
    const savingRef = useRef(false);
    const openModal = () => { dirtyRef.current = false; savingRef.current = false; setModalKey(k => k + 1); setModalOpen(true); };
    const closeModal = () => {
        if (savingRef.current) return;
        if (dirtyRef.current && !window.confirm('Hay datos sin guardar. ¿Deseas cerrar el formulario y descartarlos?')) return;
        dirtyRef.current = false;
        setModalOpen(false);
    };
    const savedFromModal = async () => {
        await search();
        dirtyRef.current = false;
        setModalOpen(false);
    };
    useEffect(() => {
        if (!modalOpen) return undefined;
        const oldOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKeyDown = (event) => { if (event.key === 'Escape') { event.preventDefault(); closeModal(); } };
        document.addEventListener('keydown', onKeyDown);
        return () => { document.body.style.overflow = oldOverflow; document.removeEventListener('keydown', onKeyDown); };
    }, [modalOpen]);
    const [rows, setRows] = useState([]);
    const [kpis, setKpis] = useState({});
    const [kpiLoading, setKpiLoading] = useState(false);
    const [showKpis, setShowKpis] = useState(true);
    const [showCharts, setShowCharts] = useState(false);
    const [charts, setCharts] = useState({ daily: [], categories: [] });
    const [chartsLoading, setChartsLoading] = useState(false);
    const [chartsError, setChartsError] = useState('');
    const [filters, setFilters] = useState(() => ({ from: todayLima(), to: todayLima() }));
    const [applied, setApplied] = useState(() => ({ from: todayLima(), to: todayLima() }));
    const [activeTab, setActiveTab] = useState('ALL');
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState('');
    const requestId = useRef(0);
    const chartsRequestId = useRef(0);
    const loadMeta = async () => {
        try { setMeta(await accountingApi.meta()); } catch (e) { setError(e?.response?.data?.message || 'No se pudieron cargar los catálogos.'); }
    };
    const fetchRows = useCallback(async (criteria) => {
        const current = ++requestId.current;
        setLoading(true);
        setError('');
        try {
            // La consulta base no restringe el tipo: los contadores y las pestañas
            // siempre usan exactamente el mismo conjunto filtrado por fecha/RUC/sede/búsqueda.
            const { operationType, movementType, ...base } = criteria;
            setKpiLoading(true);
            const [result, indicators] = await Promise.all([
                accountingApi.listMovements(base), accountingApi.movementKpis(criteria)
            ]);
            if (current === requestId.current) { setRows(result.rows || []); setKpis(indicators); }
        } catch (e) {
            if (current === requestId.current) setError(e?.response?.data?.message || 'No se pudieron consultar los movimientos.');
        } finally { if (current === requestId.current) { setLoading(false); setKpiLoading(false); } }
    }, []);
    const loadCharts = useCallback(async (criteria) => {
        const id = ++chartsRequestId.current;
        setChartsLoading(true);
        setChartsError('');
        try {
            const data = await accountingApi.movementCharts(criteria);
            if (id === chartsRequestId.current) setCharts({ daily: data.daily || [], categories: data.categories || [] });
        } catch (e) {
            if (id === chartsRequestId.current) setChartsError(e?.response?.data?.message || 'No se pudieron cargar los gráficos.');
        } finally { if (id === chartsRequestId.current) setChartsLoading(false); }
    }, []);
    const search = async () => {
        if (filters.from && filters.to && filters.from > filters.to) { setError('La fecha Desde no puede ser posterior a Hasta.'); return; }
        setApplied({ ...filters });
        setActiveTab('ALL');
        await fetchRows(filters);
        if (showCharts) loadCharts(filters);
    };
    useEffect(() => { loadMeta(); fetchRows({ from: todayLima(), to: todayLima() }); }, [fetchRows]);
    const toggleCharts = () => {
        if (!showCharts) { setShowCharts(true); loadCharts(applied); }
        else { setShowCharts(false); ++chartsRequestId.current; setChartsLoading(false); }
    };
    // Cada transferencia se muestra con sus dos partidas: salida e ingreso.
    // La vinculación por transfer_group_id se conserva para auditoría y anulación.
    const groupedRows = rows;
    const matchesType = (x, tab) => tab === 'ALL' || (tab === 'TRANSFER' ? x.movement_type === 'TRANSFER' : x.movement_type !== 'TRANSFER' && x.operation_type === tab);
    const scopedRows = groupedRows.filter(x => (!applied.movementType || x.movement_type === applied.movementType) && (!applied.operationType || (x.movement_type === 'TRANSFER' ? true : x.operation_type === applied.operationType)));
    const visibleRows = scopedRows.filter(x => matchesType(x, activeTab));
    const exportExcel = async () => {
        if (exporting) return;
        setExporting(true);
        setError('');
        try {
            // Usa los filtros aplicados, no los pendientes de pulsar Filtrar.
            // El backend devuelve el conjunto completo sin el límite de la tabla.
            const { operationType, movementType, ...base } = applied;
            const result = await accountingApi.exportMovements(base);
            const filtered = (result.rows || []).filter(x =>
                (!applied.movementType || x.movement_type === applied.movementType) &&
                (!applied.operationType || (x.movement_type === 'TRANSFER' ? true : x.operation_type === applied.operationType)) &&
                matchesType(x, activeTab)
            );
            downloadAccountingExcel(filtered, applied);
        } catch (e) {
            setError(e?.response?.data?.message || e.message || 'No se pudo exportar el reporte Excel.');
        } finally { setExporting(false); }
    };
    const tabs = [
        { id: 'ALL', label: 'Todos los movimientos' },
        { id: 'INGRESO', label: 'Ingresos' },
        { id: 'EGRESO', label: 'Egresos' },
        { id: 'TRANSFER', label: 'Transferencias' },
    ];

    return (
        <div>
            <Header namePage="accountingPage" />
            <section className="sectionBody sectionBodyAccounting">
                <div className="containerBody">
                    <main className="accountingPage">
                        <header className="accHero"><div><span>Contabilidad</span><h1>Registro de ingresos, egresos y transferencias</h1><p>Fecha habilitada desde el 03/07/2026. El rol 8 solo puede crear registros; no puede editar ni anular.</p></div></header>
                        <AccountingFilters meta={meta} filters={filters} setFilters={setFilters} onSearch={search} />
                        <AccountingKpis data={kpis} loading={kpiLoading} visible={showKpis} onToggle={() => setShowKpis(v => !v)} />
                        <section className="accFinancialChartsSection" aria-label="Análisis gráfico financiero">
                            <div className="accFinancialChartsHeading"><div><h2>Análisis financiero</h2><p>Ingresos, egresos y distribución de gastos según los filtros aplicados.</p></div><button type="button" className="accSectionToggle" aria-expanded={showCharts} aria-controls="accFinancialChartsContent" onClick={toggleCharts}>{showCharts ? 'Ocultar gráficos' : 'Mostrar gráficos'} <span aria-hidden="true">{showCharts ? '▴' : '▾'}</span></button></div>
                            {showCharts && <div id="accFinancialChartsContent"><AccountingMovementCharts data={charts} loading={chartsLoading} error={chartsError} /></div>}
                        </section>
                        <div className="accMovementToolbar"><div><h2>Movimientos registrados</h2><p>Consulta tus operaciones o registra un nuevo movimiento.</p></div><button className="accPrimary accNewMovement" type="button" onClick={openModal}>＋ Nuevo movimiento</button></div>
                        <nav className="accMovementTabs" aria-label="Filtrar movimientos por tipo">
                            {tabs.map(tab => <button key={tab.id} type="button" aria-pressed={activeTab === tab.id} className={`accMovementTab ${activeTab === tab.id ? 'is-active' : ''}`} onClick={() => setActiveTab(tab.id)}>
                                <span>{tab.label}</span><strong>{scopedRows.filter(x => matchesType(x, tab.id)).length}</strong>
                            </button>)}
                            <button type="button" className="accExcelExport" onClick={exportExcel} disabled={exporting || loading} aria-label="Descargar reporte en Excel según filtros aplicados"><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 3H19a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H8"/><path d="M3 7h10v10H3z"/><path d="m6 10 4 4m0-4-4 4"/><path d="M14 8h4m-4 4h4m-4 4h4"/></svg><span>{exporting ? 'Generando Excel...' : 'Descargar Excel'}</span></button>
                        </nav>
                        {error && <p className="accFilterError" role="alert">{error}</p>}
                        {loading && <p className="accFilterLoading" role="status">Consultando movimientos...</p>}
                        <MovementsTable rows={visibleRows} reload={() => fetchRows(applied)} permissions={meta.permissions} />
                        {modalOpen && <div className="accMovementModalBackdrop" onMouseDown={e => { if (e.target === e.currentTarget) closeModal(); }}>
                            <div className="accMovementModal" role="dialog" aria-modal="true" aria-labelledby="accMovementModalTitle">
                                <header className="accMovementModalHeader"><div><span>CONTABILIDAD · NUEVO REGISTRO</span><h2 id="accMovementModalTitle">Registrar movimiento</h2><p>Selecciona el tipo de operación y completa los datos correspondientes.</p></div><button type="button" className="accMovementModalClose" aria-label="Cerrar formulario" onClick={closeModal}>×</button></header>
                                <div className="accMovementModalBody"><MovementForm key={modalKey} meta={meta} onSaved={savedFromModal} onCancel={closeModal} onDirtyChange={value => { dirtyRef.current = value; }} onSavingChange={value => { savingRef.current = value; }} /></div>
                            </div>
                        </div>}
                    </main>
                </div>
            </section>
            <Footer />
        </div>
    );
}
